import Text from '../typography/Text';
import styles from './PermissionManager.module.scss';
import { useState, useCallback, useMemo } from 'react';
import Icon from '../Icon';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  modules,
  templateModuleKeys,
  qualityTemplateModuleKeys,
  updatePermissions,
} from './utilities';
import TextWithIcon from '../typography/TextWithIcon';
import Searchbar from '../Searchbar';
import { Form } from 'react-bootstrap';
import UniversalDropdown from '../dropdownV2/UniversalDropdown';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { useCurrentUser } from '../../providers/UserProvider';
import MultiAvatar from '../MultiAvatar';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import { customColors } from '../../utilities';
import classNames from 'classnames';
import Spacer from '../typography/Spacer';
import { useTemplates } from '../../providers/TemplateProvider';
import { Tooltip } from '@mui/material';
import WorkspaceAdminStrip from './WorkspaceAdminStrip';
import HighlightText from './HighlightText';

export default function PermissionManager({
  fontSize = 'md',
  padding = 'sm',
  roles = [],
  user = null,
  role = {},
  show = false,
  parent = 'page',
  currentPermissions = {},
  setResult = () => {},
  readOnly = false,
  updateMutation = () => {},
  allUsers = [],
  updateUserMutation = () => {},
}) {
  const isMatrixMode = parent === 'page' || parent === 'full';
  const object = user ?? role;
  const { company, assessmentTypes } = useCurrentUser();
  const { t } = useTranslation();
  const [moduleSearch, setModuleSearch] = useState(null);
  const [showSearch, setShowSearch] = useState(parent === 'full');
  const flags = useFlags();
  const { companyAdmins } = useWorkspacePermissions();
  const [expandedModule, setExpandedModule] = useState(null);

  const hasPermissionsAssigned = (module) => {
    const key = module[0] || null;
    return roles?.some(
      (role) =>
        key &&
        role?.permissions &&
        Array.isArray(role?.permissions[key]) &&
        role?.permissions[key]?.length > 0,
    );
  };

  const getTitleText = useCallback(
    (module) => {
      const { keys = [] } = module || {};

      const [primaryKey] = keys;

      switch (primaryKey) {
        case 'incident':
          return company?.incidentName || t(module.title);
        case 'observation':
          return company?.observationName || t(module.title);
        case 'inspection':
          return company?.assessmentName || t(module.title);
        case 'toolboxTalk':
          return company?.toolboxName || t(module.title);
        case 'checkIn':
          return company?.checkInName || t(module.title);
        case 'riskRegistry':
          return company?.riskRegistryName || t(module.title);
        case 'audit':
          return company?.auditName || t(module.title);
        case 'file':
          return company?.filesName || t(module.title);
        default:
          return t(module.title);
      }
    },
    [company, t],
  );

  const rolesForModule = useCallback(
    (module, option, type) => {
      if (module?.keys.includes('inspection')) {
        const actions = [];
        for (const optionAction of option?.actions) {
          actions.push(`${optionAction}|${type}`);
        }
        return roles
          ?.map(
            (role) =>
              (role.permissions?.[module?.keys[0]]?.includes(...actions) ||
                role.permissions?.[module?.keys[1]]?.includes(...actions)) &&
              role,
          )
          .filter(Boolean);
      } else {
        return roles
          ?.map(
            (role) =>
              (role.permissions?.[module?.keys[0]]?.includes(
                ...option?.actions,
              ) ||
                role.permissions?.[module?.keys[1]]?.includes(
                  ...option?.actions,
                )) &&
              role,
          )
          .filter(Boolean);
      }
    },
    [roles],
  );

  // Mirrors rolesForModule above, but for users individually granted this
  // permission (not through any role).
  const usersForModule = useCallback(
    (module, option, type) => {
      if (module?.keys.includes('inspection')) {
        const actions = [];
        for (const optionAction of option?.actions) {
          actions.push(`${optionAction}|${type}`);
        }
        return (
          allUsers?.filter(
            (u) =>
              u.permissions?.[module?.keys[0]]?.includes(...actions) ||
              u.permissions?.[module?.keys[1]]?.includes(...actions),
          ) ?? []
        );
      } else {
        return (
          allUsers?.filter(
            (u) =>
              u.permissions?.[module?.keys[0]]?.includes(...option?.actions) ||
              u.permissions?.[module?.keys[1]]?.includes(...option?.actions),
          ) ?? []
        );
      }
    },
    [allUsers],
  );

  // "Search bar" doubling as a person lookup: full mode only
  const matchedUsers = useMemo(() => {
    if (parent !== 'full' || !moduleSearch?.length) return [];
    const query = moduleSearch.toLowerCase();
    return (
      allUsers?.filter((u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(query),
      ) ?? []
    );
  }, [parent, moduleSearch, allUsers]);

  const matchedRoles = useMemo(() => {
    if (parent !== 'full' || !moduleSearch?.length) return [];
    const query = moduleSearch.toLowerCase();
    return roles?.filter((r) => r.title?.toLowerCase().includes(query)) ?? [];
  }, [parent, moduleSearch, roles]);

  const { activeTemplateTypes } = useTemplates();
  const permissableTemplates = useMemo(() => {
    return Object.values(assessmentTypes).filter((f) =>
      activeTemplateTypes.includes(f.key),
    );
  }, [activeTemplateTypes, assessmentTypes]);

  const personOrRoleMatchedModules = useMemo(() => {
    if (!matchedUsers.length && !matchedRoles.length) return null;

    const templateTypes = Object.values(permissableTemplates || {}).filter(
      ({ key }) =>
        !templateModuleKeys.includes(key) &&
        !(flags.enableQualityModule && qualityTemplateModuleKeys.includes(key)),
    );

    return modules.filter((module) => {
      if (module?.flag && !flags[module.flag]) return false;

      const isInspectionFamily = module?.keys?.includes('inspection');
      const typesToCheck = isInspectionFamily ? templateTypes : [null];

      return module?.options?.some((option) => {
        if (option?.flag && !flags[option.flag]) return false;

        return typesToCheck.some((type) => {
          const grantedUsers = usersForModule(module, option, type?.key) || [];
          const grantedRoles = rolesForModule(module, option, type?.key) || [];

          const userMatch = matchedUsers.some(
            (mu) =>
              grantedUsers.some((gu) => gu.id === mu.id) ||
              grantedRoles.some((gr) => gr.users?.some((u) => u.id === mu.id)),
          );
          if (userMatch) return true;

          return matchedRoles.some((mr) =>
            grantedRoles.some((gr) => gr.id === mr.id),
          );
        });
      });
    });
  }, [
    matchedUsers,
    matchedRoles,
    flags,
    permissableTemplates,
    usersForModule,
    rolesForModule,
  ]);

  const filteredModules = useMemo(() => {
    const reducedModules = readOnly
      ? modules?.filter(({ keys }) =>
          keys?.every(
            (key) =>
              Object.keys(currentPermissions ?? {}).includes(key) &&
              currentPermissions[key]?.length > 0,
          ),
        )
      : modules;

    const searched = personOrRoleMatchedModules
      ? reducedModules?.filter((m) => personOrRoleMatchedModules.includes(m))
      : !moduleSearch?.length
      ? reducedModules
      : reducedModules?.filter((m) => {
          const query = moduleSearch.toLowerCase();
          if (t(m.title).toLowerCase().includes(query)) return true;
          if (
            m.options?.some((o) => t(o.title).toLowerCase().includes(query))
          ) {
            return true;
          }
          return !!m.automaticActions?.some((a) =>
            a.title?.toLowerCase().includes(query),
          );
        });

    if (parent !== 'full' || !searched) return searched;

    return [...searched].sort((a, b) => {
      const aIsAssessments = a?.keys?.includes('inspection');
      const bIsAssessments = b?.keys?.includes('inspection');
      if (aIsAssessments && !bIsAssessments) return 1;
      if (bIsAssessments && !aIsAssessments) return -1;
      return getTitleText(a).localeCompare(getTitleText(b));
    });
  }, [
    moduleSearch,
    personOrRoleMatchedModules,
    readOnly,
    currentPermissions,
    t,
    parent,
    getTitleText,
  ]);

  const isPermissionChecked = useCallback(
    (module, option, type) => {
      if (currentPermissions === null) return false;
      if (module?.keys.includes('inspection')) {
        const actions = [];
        for (const optionAction of option?.actions) {
          actions.push(`${optionAction}|${type}`);
        }
        return (
          currentPermissions[module?.keys[0]]?.includes(...actions) ||
          currentPermissions[module?.keys[1]]?.includes(...actions)
        );
      } else if (!!currentPermissions[module?.keys[0]]) {
        return (
          currentPermissions[module?.keys[0]]?.includes(...option?.actions) ||
          currentPermissions[module?.keys[1]]?.includes(...option?.actions)
        );
      } else {
        return false;
      }
    },
    [currentPermissions],
  );

  const existingAssessmentTypePermissions = useMemo(() => {
    if (currentPermissions === null) return [];
    const inspectionPermissions = currentPermissions?.inspection || [];

    const typesToInclude = [];

    for (const permission of inspectionPermissions) {
      const type = permission.split('|')[1];
      typesToInclude.push(type);
    }

    return Array.from(new Set(typesToInclude));
  }, [currentPermissions]);

  const formatRolesForSelect = (roles) => {
    return roles?.length > 0
      ? roles.map((role) => ({
          value: `role-${role.id}`,
          id: role.id,
          entityType: 'role',
          title: role.title,
        }))
      : [];
  };

  const formatUsersForSelect = (users) => {
    return users?.length > 0
      ? users.map((u) => ({
          value: `user-${u.id}`,
          id: u.id,
          entityType: 'user',
          firstName: u.firstName,
          lastName: u.lastName,
          avatarUrl: u.avatarUrl,
        }))
      : [];
  };

  const getModuleKey = (module, option) =>
    option?.actions?.includes('MANAGE_TEMPLATE') ||
    option?.actions?.some((action) => action.includes('MANAGE_TEMPLATE'))
      ? module?.keys?.find((m) => m === 'inspectionTemplate')
      : module?.keys?.find((m) => m !== 'inspectionTemplate');

  // Combined role + individual-user editor for the page view. A
  // single UniversalDropdown shows both groups
  const handlePermissionGrantChange = (
    updatedOptions,
    module,
    option,
    type,
  ) => {
    const previousOptions = [
      ...formatRolesForSelect(rolesForModule(module, option, type)),
      ...formatUsersForSelect(usersForModule(module, option, type)),
    ];

    const added = updatedOptions.filter(
      (o) => !previousOptions.some((p) => p.value === o.value),
    );
    const removed = previousOptions.filter(
      (o) => !updatedOptions.some((u) => u.value === o.value),
    );

    const modelKey = getModuleKey(module, option);

    [...added.map((o) => ({ ...o, changeAction: 'ADD' }))]
      .concat(removed.map((o) => ({ ...o, changeAction: 'REMOVE' })))
      .forEach(({ entityType, id, changeAction }) => {
        if (entityType === 'user') {
          const targetUser = allUsers.find((u) => u.id === id);
          if (!targetUser) return;
          updatePermissions({
            object: targetUser,
            objectIdKey: 'userId',
            mutation: updateUserMutation,
            options: { key: modelKey, actions: option?.actions, type },
            setResult,
            action: changeAction,
          });
        } else {
          const targetRole = roles.find((r) => r.id === id);
          if (!targetRole) return;
          updatePermissions({
            object: targetRole,
            objectIdKey: 'roleId',
            mutation: updateMutation,
            options: { key: modelKey, actions: option?.actions, type },
            setResult,
            action: changeAction,
          });
        }
      });
  };

  const RoleUsersAvatars = ({ users }) =>
    users?.length ? (
      <div className={styles.roleUsersGroup}>
        <Tooltip
          title="Members of the role(s) granted this permission above"
          arrow
        >
          <span className={styles.roleUsersInfoIconWrapper}>
            <Icon size="xxs" className={styles.roleUsersInfoIcon}>
              info
            </Icon>
          </span>
        </Tooltip>
        <MultiAvatar size="sm" users={users} maxLength={8} />
      </div>
    ) : null;

  // Most real access in this app comes from these relationships, not from
  // grants
  const OverridesRow = ({ overrides }) =>
    overrides?.length ? (
      <div className={styles.overridesRow}>
        <Text size="xs" color="secondary" noMargin>
          Already automatic for:
        </Text>
        {overrides.map((override) => (
          <span key={override} className={styles.overrideChip}>
            {override}
          </span>
        ))}
      </div>
    ) : null;

  // Full mode only: the explanatory paragraph used to render inline
  const DescriptionTooltip = ({ text }) =>
    text ? (
      <Tooltip title={text} arrow>
        <span className={styles.descriptionInfoIconWrapper}>
          <Icon size="xxs" className={styles.descriptionInfoIcon}>
            info
          </Icon>
        </span>
      </Tooltip>
    ) : null;

  // Full mode only: replaces the always-open searchable dropdown
  const GrantEditor = ({
    isEditing,
    dropdownOptions,
    selected,
    selectedRoles,
    selectedGrantedUsers,
    roleUsers,
    onChange,
  }) => {
    const hasAny = !!(selectedRoles?.length || selectedGrantedUsers?.length);

    return isEditing ? (
      <div className={styles.flex}>
        <UniversalDropdown
          type="permissionGrant"
          isMulti
          placeholder={t(keys.permissions.SEARCH_FOR_ROLE)}
          options={dropdownOptions}
          selected={selected}
          onChange={onChange}
        />
        <RoleUsersAvatars users={roleUsers} />
      </div>
    ) : (
      <div className={styles.grantSummary}>
        {hasAny ? (
          <>
            {selectedRoles.map((role) => {
              const memberNames = role.users
                ?.map((u) => `${u.firstName} ${u.lastName}`)
                .join(', ');
              return (
                <Tooltip
                  key={`role-${role.id}`}
                  title={memberNames?.length ? memberNames : 'No members yet'}
                  arrow
                >
                  <span className={styles.grantChip}>
                    <HighlightText text={role.title} query={moduleSearch} />
                  </span>
                </Tooltip>
              );
            })}
            {selectedGrantedUsers?.map((grantedUser) => (
              <span key={`user-${grantedUser.id}`} className={styles.grantChip}>
                <HighlightText
                  text={`${grantedUser.firstName} ${grantedUser.lastName}`}
                  query={moduleSearch}
                />
              </span>
            ))}
          </>
        ) : (
          <Text size="xs" color="secondary" noMargin>
            Nobody yet
          </Text>
        )}
      </div>
    );
  };

  const ModuleCard = ({ module, index }) => {
    const [isEditing, setIsEditing] = useState(false);

    return (
      <div
        key={`${index}-${module?.title}-card`}
        className={classNames(
          styles.module,
          styles[padding],
          parent === 'full' && styles.fullModuleCard,
          // Assessments repeats its full option set once per template type
          parent === 'full' &&
            module?.keys.includes('inspection') &&
            styles.fullModuleCardWide,
        )}
      >
        <div className={styles.header}>
          <div>
            <TextWithIcon
              noMargin
              weight="semiBold"
              size={fontSize || isMatrixMode ? 'md-lg' : 'md'}
              leftIcon={module?.icon}
            >
              <HighlightText text={getTitleText(module)} query={moduleSearch} />
            </TextWithIcon>
          </div>
          <div>
            {parent !== 'full' && (
              <Icon
                style={{
                  color: hasPermissionsAssigned(module.keys)
                    ? customColors.GREEN_LIGHT
                    : customColors.SECONDARY_LIGHT,
                }}
              >
                {!hasPermissionsAssigned(module.keys) ? '' : 'check_circle'}
              </Icon>
            )}
            {parent === 'full' && !!module?.options?.length && (
              <Icon
                hover
                className={styles.cardEditToggle}
                onClick={() => setIsEditing((editing) => !editing)}
              >
                {isEditing ? 'close' : 'edit'}
              </Icon>
            )}
            {parent !== 'full' && (
              <Icon
                onClick={() => {
                  if (expandedModule === module.title) {
                    setExpandedModule(null);
                  } else {
                    setExpandedModule(module.title);
                  }
                }}
              >
                {expandedModule === module.title
                  ? 'expand_less'
                  : 'expand_more'}
              </Icon>
            )}
          </div>
        </div>
        {parent === 'full' && module?.note && (
          <Text size="sm" color="secondary" className={styles.fullModuleNote}>
            {module.note}
          </Text>
        )}
        {parent === 'full' && (
          <>
            <Spacer size="xs" />
            <Text
              size="xs"
              weight="semiBold"
              color="green"
              noMargin
              className={styles.sectionLabel}
            >
              Automatic access
            </Text>
            <div className={styles.options}>
              {module.automaticActions?.length ? (
                module.automaticActions.map((item) => (
                  <div className={styles.moduleContainer} key={item.title}>
                    <div className={styles.flexrow}>
                      <Text weight="semiBold" size="sm" noMargin>
                        <HighlightText text={item.title} query={moduleSearch} />
                      </Text>
                      <DescriptionTooltip text={item.description} />
                    </div>
                    <OverridesRow overrides={item.overrides} />
                  </div>
                ))
              ) : (
                <Text size="sm" color="secondary" noMargin>
                  Nothing.
                </Text>
              )}
            </div>
            {!!module?.options?.length && (
              <>
                <Spacer size="sm" />
                <Text
                  size="xs"
                  weight="semiBold"
                  color="secondary"
                  noMargin
                  className={styles.sectionLabel}
                >
                  Assigned access
                </Text>
              </>
            )}
          </>
        )}

        {(parent === 'full' && !!module?.options?.length) ||
        expandedModule === module.title ||
        expandedModule === 'ALL' ? (
          <div className={styles.options}>
            <Spacer size="xs" />
            {module?.keys.includes('inspection') &&
            !!permissableTemplates?.length ? (
              <div
                className={
                  parent === 'full' ? styles.templateTypeGrid : undefined
                }
              >
                {Object.values(permissableTemplates)
                  .filter(({ key }) =>
                    readOnly
                      ? existingAssessmentTypePermissions.includes(key)
                      : true,
                  )
                  .filter(({ key }) => !templateModuleKeys.includes(key))
                  .filter(
                    ({ key }) =>
                      !(
                        flags.enableQualityModule &&
                        qualityTemplateModuleKeys.includes(key)
                      ),
                  )
                  .map((type, i) => (
                    <div
                      key={`${type?.title}-${i}`}
                      className={
                        parent === 'full' ? styles.templateTypeCard : undefined
                      }
                    >
                      <div className={styles.title}>
                        <TextWithIcon
                          noMargin
                          weight="semiBold"
                          size={fontSize}
                          leftIcon={type.icon}
                          color={type.color}
                        >
                          {type?.title ? t(type.title) : type.name}
                        </TextWithIcon>
                      </div>

                      <div className={styles[isMatrixMode ? 'page' : parent]}>
                        {module?.options?.map((option) => {
                          if (option?.flag && !flags[option.flag]) return null;

                          const selectedRoles =
                            rolesForModule(module, option, type?.key) || [];
                          const selectedGrantedUsers =
                            usersForModule(module, option, type?.key) || [];
                          const roleUsers =
                            selectedRoles?.map(({ users }) => users).flat() ||
                            [];

                          return (
                            <div
                              className={styles.moduleContainer}
                              key={`${option?.title}-${i}`}
                            >
                              <div className={styles.flexrow}>
                                {!isMatrixMode ? (
                                  <Form.Check
                                    disabled={readOnly}
                                    checked={isPermissionChecked(
                                      module,
                                      option,
                                      type?.key,
                                    )}
                                    onChange={({ target: { checked } }) =>
                                      updatePermissions({
                                        object: {
                                          ...object,
                                          permissions: {
                                            ...currentPermissions,
                                          },
                                        },
                                        objectIdKey: user ? 'userId' : 'roleId',
                                        mutation: updateMutation,
                                        options: {
                                          key: getModuleKey(module, option),
                                          actions: option?.actions,
                                          type: type?.key,
                                        },
                                        setResult,
                                        action: checked ? 'ADD' : 'REMOVE',
                                      })
                                    }
                                  />
                                ) : null}
                                <Text>
                                  <HighlightText
                                    text={t(option?.title)}
                                    query={moduleSearch}
                                  />
                                </Text>
                                {parent === 'full' && (
                                  <DescriptionTooltip
                                    text={option?.description}
                                  />
                                )}
                              </div>
                              {parent === 'full' && (
                                <OverridesRow overrides={option?.overrides} />
                              )}
                              {parent === 'full' ? (
                                <GrantEditor
                                  isEditing={isEditing}
                                  dropdownOptions={[
                                    ...formatRolesForSelect(roles),
                                    ...formatUsersForSelect(allUsers),
                                  ]}
                                  selected={[
                                    ...formatRolesForSelect(selectedRoles),
                                    ...formatUsersForSelect(
                                      selectedGrantedUsers,
                                    ),
                                  ]}
                                  selectedRoles={selectedRoles}
                                  selectedGrantedUsers={selectedGrantedUsers}
                                  roleUsers={roleUsers}
                                  onChange={(updatedOptions) =>
                                    handlePermissionGrantChange(
                                      updatedOptions,
                                      module,
                                      option,
                                      type?.key,
                                    )
                                  }
                                />
                              ) : isMatrixMode ? (
                                <div className={styles.flex}>
                                  <UniversalDropdown
                                    type="permissionGrant"
                                    isMulti
                                    placeholder={t(
                                      keys.permissions.SEARCH_FOR_ROLE,
                                    )}
                                    options={[
                                      ...formatRolesForSelect(roles),
                                      ...formatUsersForSelect(allUsers),
                                    ]}
                                    selected={[
                                      ...formatRolesForSelect(selectedRoles),
                                      ...formatUsersForSelect(
                                        selectedGrantedUsers,
                                      ),
                                    ]}
                                    onChange={(updatedOptions) =>
                                      handlePermissionGrantChange(
                                        updatedOptions,
                                        module,
                                        option,
                                        type?.key,
                                      )
                                    }
                                  />
                                  <RoleUsersAvatars users={roleUsers} />
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className={styles.flex}>
                <div className={styles[isMatrixMode ? 'page' : parent]}>
                  {module?.options?.map((option, i) => {
                    if (option?.flag && !flags[option.flag]) return null;

                    const nonTemplateKey = module?.keys?.find(
                      (k) => k !== 'inspectionTemplate',
                    );
                    const selectedRoles =
                      rolesForModule(module, option, nonTemplateKey) || [];
                    const selectedGrantedUsers =
                      usersForModule(module, option, nonTemplateKey) || [];
                    const roleUsers =
                      selectedRoles?.map(({ users }) => users).flat() || [];

                    return (
                      <div
                        className={styles.moduleContainer}
                        key={`${option?.title}-${i}`}
                      >
                        <div className={styles.flexrow}>
                          {!isMatrixMode ? (
                            <Form.Check
                              disabled={readOnly}
                              checked={isPermissionChecked(module, option)}
                              onChange={({ target: { checked } }) =>
                                updatePermissions({
                                  object: {
                                    ...object,
                                    permissions: { ...currentPermissions },
                                  },
                                  objectIdKey: user ? 'userId' : 'roleId',
                                  mutation: updateMutation,
                                  options: {
                                    key: getModuleKey(module, option),
                                    actions: option?.actions,
                                  },
                                  setResult,
                                  action: checked ? 'ADD' : 'REMOVE',
                                })
                              }
                            />
                          ) : null}
                          <Text noMargin>
                            <HighlightText
                              text={t(option?.title)}
                              query={moduleSearch}
                            />
                          </Text>
                          {parent === 'full' && (
                            <DescriptionTooltip text={option?.description} />
                          )}
                        </div>
                        {parent === 'full' && (
                          <OverridesRow overrides={option?.overrides} />
                        )}
                        {parent === 'full' ? (
                          <GrantEditor
                            isEditing={isEditing}
                            dropdownOptions={[
                              ...formatRolesForSelect(roles),
                              ...formatUsersForSelect(allUsers),
                            ]}
                            selected={[
                              ...formatRolesForSelect(selectedRoles),
                              ...formatUsersForSelect(selectedGrantedUsers),
                            ]}
                            selectedRoles={selectedRoles}
                            selectedGrantedUsers={selectedGrantedUsers}
                            roleUsers={roleUsers}
                            onChange={(updatedOptions) =>
                              handlePermissionGrantChange(
                                updatedOptions,
                                module,
                                option,
                              )
                            }
                          />
                        ) : isMatrixMode ? (
                          <div className={styles.flex}>
                            <UniversalDropdown
                              type="permissionGrant"
                              isMulti
                              placeholder={t(keys.permissions.SEARCH_FOR_ROLE)}
                              options={[
                                ...formatRolesForSelect(roles),
                                ...formatUsersForSelect(allUsers),
                              ]}
                              selected={[
                                ...formatRolesForSelect(selectedRoles),
                                ...formatUsersForSelect(selectedGrantedUsers),
                              ]}
                              onChange={(updatedOptions) =>
                                handlePermissionGrantChange(
                                  updatedOptions,
                                  module,
                                  option,
                                )
                              }
                            />
                            <RoleUsersAvatars users={roleUsers} />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    show && (
      <div className={styles.container}>
        <div className={styles.header}>
          {parent !== 'full' && (
            <div>
              <Text
                weight={isMatrixMode ? 'bold' : 'semiBold'}
                size={isMatrixMode ? 'md-lg' : 'md'}
                noMargin
              >
                Permissions
              </Text>
              <Text size="sm" color="secondary" noMargin>
                {t(keys.permissions.COMPANY_ADMIN_TEXT)}
              </Text>
            </div>
          )}
          <div>
            <div className={styles.searchbar}>
              {showSearch && (
                <Searchbar
                  className={styles.searchWidth}
                  value={moduleSearch}
                  onChange={setModuleSearch}
                />
              )}
              {parent !== 'full' && (
                <Icon
                  className={styles.searchIcon}
                  style={{ fontSize: '1.2rem' }}
                  onClick={() =>
                    showSearch
                      ? (setModuleSearch(''), setShowSearch(!showSearch))
                      : setShowSearch(!showSearch)
                  }
                >
                  {showSearch ? 'close' : 'search'}
                </Icon>
              )}
              {parent !== 'full' && (
                <TextWithIcon
                  icon={
                    expandedModule === 'ALL' ? 'expand_less' : 'expand_more'
                  }
                  noMargin
                  size="sm"
                  weight="semiBold"
                  color="accentPrimary"
                  hover
                  onClick={() => {
                    if (expandedModule === 'ALL') {
                      setExpandedModule(null);
                    } else {
                      setExpandedModule('ALL');
                    }
                  }}
                >
                  {expandedModule === 'ALL' ? 'Collapse All' : 'Expand All'}
                </TextWithIcon>
              )}
            </div>
          </div>
        </div>
        <Spacer />
        {isMatrixMode && (
          <div className={classNames(styles.module, styles[padding])}>
            <div className={styles.header}>
              <div>
                <TextWithIcon
                  leftIcon={'group'}
                  size={isMatrixMode ? 'md-lg' : 'md'}
                  noMargin
                  weight="semiBold"
                >
                  Company Admins
                </TextWithIcon>
                <Text size="sm" color="secondary" noMargin>
                  Automatic access to everything below, in every workspace.
                </Text>
              </div>
              <MultiAvatar
                size="sm"
                users={companyAdmins || []}
                maxLength={20}
              />
            </div>
          </div>
        )}
        {parent === 'full' && (
          <>
            <Spacer />
            <div className={classNames(styles.module, styles[padding])}>
              <TextWithIcon
                leftIcon={'admin_panel_settings'}
                size="md-lg"
                noMargin
                weight="semiBold"
              >
                Workspace Admins
              </TextWithIcon>
              <Text size="sm" color="secondary" noMargin>
                Elevated access within their own workspace only — not automatic
                company-wide, but called out as "Workspace Admin" throughout the
                modules below wherever it applies.
              </Text>
              <Spacer size="xs" />
              <WorkspaceAdminStrip searchQuery={moduleSearch} />
            </div>
          </>
        )}

        <div className={parent === 'full' ? styles.fullGrid : styles.modules}>
          {filteredModules?.length > 0 ? (
            filteredModules.map(
              (module, index) =>
                ((module?.flag && flags[module?.flag]) || !module?.flag) && (
                  <ModuleCard
                    key={`${index}-${module?.title}-card`}
                    module={module}
                    index={index}
                  />
                ),
            )
          ) : (
            <div>
              <br />
              <Text noMargin textAlign="center" color="secondary">
                {t(keys.common.NONE)}
              </Text>
            </div>
          )}
        </div>
      </div>
    )
  );
}
